import { useEffect, useMemo, useState } from 'react'
import { useConnectionQualityIndicator, useConnectionState, useLocalParticipant, useRoomContext } from '@livekit/components-react'
import { Track } from 'livekit-client'

import { createLevelMeter } from '../deviceProbe'
import { detectCanScreenShare, type ShareFailure } from '../screenShare'
import { supportsAudioOutputSelection } from '../audioOutput'
import { useAudioUnlock } from '../useAudioUnlock'
import { camVerdict, connectionVerdict, micVerdict, shareVerdict, speakerVerdict } from './setupVerdicts'
import type { useMediaRecovery } from '../mediaRecovery'

export function useSetupChecks({ open, recovery, shareFailure, boardOpen, sharing }: { open: boolean; recovery: ReturnType<typeof useMediaRecovery>; shareFailure: ShareFailure | null; boardOpen: boolean; sharing: boolean }) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant()
  const room = useRoomContext()
  const { quality } = useConnectionQualityIndicator({ participant: localParticipant })
  const state = useConnectionState()
  const audio = useAudioUnlock(room)
  const micPublication = localParticipant?.getTrackPublication(Track.Source.Microphone)
  const cameraPublication = localParticipant?.getTrackPublication(Track.Source.Camera)
  const [level, setLevel] = useState<number | null>(null)

  useEffect(() => {
    const track = micPublication?.track?.mediaStreamTrack
    if (!open || !track) { setLevel(null); return }
    const meter = createLevelMeter(new MediaStream([track]))
    if (!meter) return
    let frame = 0
    const tick = () => { setLevel(meter.level()); frame = requestAnimationFrame(tick) }
    frame = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(frame); meter.stop() }
  }, [open, micPublication?.track])

  return useMemo(() => ({
    mic: micVerdict({ published: Boolean(micPublication), enabled: isMicrophoneEnabled, access: recovery.lastMicAccess, level, quietForMs: 0 }),
    cam: camVerdict({ published: Boolean(cameraPublication), enabled: isCameraEnabled, access: recovery.lastCamAccess, frozen: recovery.cameraFrozen }),
    speaker: speakerVerdict({ supported: supportsAudioOutputSelection(), blocked: audio.blocked }),
    share: shareVerdict({ supported: detectCanScreenShare(), sharing, boardOpen, lastFailure: shareFailure }),
    connection: connectionVerdict({ quality, state }),
    level,
  }), [audio.blocked, boardOpen, cameraPublication, isCameraEnabled, isMicrophoneEnabled, level, micPublication, quality, recovery.cameraFrozen, recovery.lastCamAccess, recovery.lastMicAccess, shareFailure, sharing, state])
}
